import { lazy } from 'react';
import { createBrowserRouter, RouterProvider, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import EditorRoute from './components/EditorRoute';
import StudentRoute from './components/StudentRoute';
import ThemesRoute from './components/ThemesRoute';
import AdminRoute from './components/AdminRoute';
import DashboardOrRedirect from './components/DashboardOrRedirect';
import RouteErrorPage from './components/RouteErrorPage';
import './App.css';

// Страницы грузятся отдельными файлами: в первый бандл попадает только оболочка сайта.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const TaskListPage = lazy(() => import('./pages/TaskListPage'));
const TaskDetailPage = lazy(() => import('./pages/TaskDetailPage'));
const TaskEditorPage = lazy(() => import('./pages/TaskEditorPage'));
const BulkImportPage = lazy(() => import('./pages/BulkImportPage'));
const CollaborativePage = lazy(() => import('./pages/CollaborativePage'));
const WorkPage = lazy(() => import('./pages/WorkPage'));
const MyTasksPage = lazy(() => import('./pages/MyTasksPage'));
const MyAttemptsPage = lazy(() => import('./pages/MyAttemptsPage'));
const MyLessonsPage = lazy(() => import('./pages/MyLessonsPage'));
const QuizReviewPage = lazy(() => import('./pages/QuizReviewPage'));
const MySchedulePage = lazy(() => import('./pages/MySchedulePage'));
const MyMessagesPage = lazy(() => import('./pages/MyMessagesPage'));
const TeacherMessagesPage = lazy(() => import('./pages/TeacherMessagesPage'));
const PublicSchedulePage = lazy(() => import('./pages/PublicSchedulePage'));
const TeacherDashboardPage = lazy(() => import('./pages/TeacherDashboardPage'));
const TeacherSchedulePage = lazy(() => import('./pages/TeacherSchedulePage'));
const TeacherStudentWorkPage = lazy(() => import('./pages/TeacherStudentWorkPage'));
const TeacherStudentQuestionnairePage = lazy(() => import('./pages/TeacherStudentQuestionnairePage'));
const TeacherStudentActivityPage = lazy(() => import('./pages/TeacherStudentActivityPage'));
const TeacherListCreatePage = lazy(() => import('./pages/TeacherListCreatePage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const QuestionnairePage = lazy(() => import('./pages/QuestionnairePage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const MyTeachersPage = lazy(() => import('./pages/MyTeachersPage'));
const StudentTopicProgressPage = lazy(() => import('./pages/StudentTopicProgressPage'));
const TaskCatalogPage = lazy(() => import('./pages/TaskCatalogPage'));
const TaskCatalogPrintPage = lazy(() => import('./pages/TaskCatalogPrintPage'));
const EditorSettingsPage = lazy(() => import('./pages/EditorSettingsPage'));
const TeacherTagsPage = lazy(() => import('./pages/TeacherTagsPage'));
const TeacherEgeMapPage = lazy(() => import('./pages/TeacherEgeMapPage'));
const TeacherNewUsersPage = lazy(() => import('./pages/TeacherNewUsersPage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const TeacherNewUserActivityPage = lazy(() => import('./pages/TeacherNewUserActivityPage'));
const TopicProgramPage = lazy(() => import('./pages/TopicProgramPage'));
const OgePythonPathPage = lazy(() => import('./pages/OgePythonPathPage'));
const GameCreationPathPage = lazy(() => import('./pages/GameCreationPathPage'));
const AdminTopicProgramsPage = lazy(() => import('./pages/AdminTopicProgramsPage'));
const TaskBankCleanupPage = lazy(() => import('./pages/TaskBankCleanupPage'));
const TeacherToolsPage = lazy(() => import('./pages/TeacherToolsPage'));
const TeacherTaskInboxPage = lazy(() => import('./pages/TeacherTaskInboxPage'));
const TaskBankFamiliesPage = lazy(() => import('./pages/TaskBankFamiliesPage'));
const KonspektyListPage = lazy(() => import('./pages/KonspektyListPage'));
const KonspektDetailPage = lazy(() => import('./pages/KonspektDetailPage'));
const KonspektFormPage = lazy(() => import('./pages/KonspektFormPage'));
const PracticeQuizzesPage = lazy(() => import('./pages/PracticeQuizzesPage'));
const TrainersPage = lazy(() => import('./pages/TrainersPage'));
const TrainerSessionPage = lazy(() => import('./pages/TrainerSessionPage'));
const SlidingWindowPage = lazy(() => import('./pages/SlidingWindowPage'));
const RecursionDemoPage = lazy(() => import('./pages/RecursionDemoPage'));
const GameDemoPage = lazy(() => import('./pages/GameDemoPage'));
const ArticlesListPage = lazy(() => import('./pages/ArticlesListPage'));
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage'));
const AdminArticlesPage = lazy(() => import('./pages/AdminArticlesPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const BillingReturnPage = lazy(() => import('./pages/BillingReturnPage'));
const TeacherBillingPage = lazy(() => import('./pages/TeacherBillingPage'));
const TeacherStudentBillingPage = lazy(() => import('./pages/TeacherStudentBillingPage'));
const ParentHomePage = lazy(() => import('./pages/ParentHomePage'));
const ParentChildPage = lazy(() => import('./pages/ParentChildPage'));
const ParentChildTabRedirect = lazy(() =>
  import('./pages/ParentChildPage').then((mod) => ({ default: mod.ParentChildTabRedirect })),
);
const ParentInvitePage = lazy(() => import('./pages/ParentInvitePage'));
const ChildInvitePage = lazy(() => import('./pages/ChildInvitePage'));
const ParentQuestionnairePage = lazy(() => import('./pages/ParentQuestionnairePage'));
const MiniGroupsPage = lazy(() => import('./pages/MiniGroupsPage'));
const HowIWorkPage = lazy(() => import('./pages/HowIWorkPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const ConsentPage = lazy(() => import('./pages/ConsentPage'));
const OfertaPage = lazy(() => import('./pages/OfertaPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function GuestOrPrivateWork() {
  const [searchParams] = useSearchParams();
  if (searchParams.get('guest') === '1') {
    return <WorkPage />;
  }
  return (
    <PrivateRoute>
      <WorkPage />
    </PrivateRoute>
  );
}

/** Data Router: нужен для useBlocker на странице работы с задачей (WorkPage). */
const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        // Сбой страницы остаётся в основной колонке: шапка и меню живы.
        // Сбой самой шапки ловит errorElement корня.
        errorElement: <RouteErrorPage />,
        children: [
      { index: true, element: <PrivateRoute><DashboardOrRedirect /></PrivateRoute> },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password/:uid/:token', element: <ResetPasswordPage /> },
      { path: 'mini', element: <MiniGroupsPage /> },
      { path: 'kak', element: <HowIWorkPage /> },
      { path: 'privacy', element: <PrivacyPage /> },
      { path: 'consent', element: <ConsentPage /> },
      { path: 'oferta', element: <OfertaPage /> },
      { path: 'terms', element: <TermsPage /> },
      { path: 'schedule/public/:token', element: <PublicSchedulePage /> },
      { path: 'profile', element: <PrivateRoute><ProfilePage /></PrivateRoute> },
      { path: 'profile/delete', element: <PrivateRoute><DeleteAccountPage /></PrivateRoute> },
      { path: 'questionnaire', element: <PrivateRoute><QuestionnairePage /></PrivateRoute> },
      { path: 'billing/return', element: <PrivateRoute><BillingReturnPage /></PrivateRoute> },
      { path: 'billing', element: <PrivateRoute><BillingPage /></PrivateRoute> },
      { path: 'parent/children/:studentId/topic-progress', element: <PrivateRoute><ParentChildTabRedirect tab="topics" /></PrivateRoute> },
      { path: 'parent/children/:studentId/activity', element: <PrivateRoute><ParentChildTabRedirect tab="activity" /></PrivateRoute> },
      { path: 'parent/children/:studentId/questionnaire', element: <PrivateRoute><ParentQuestionnairePage /></PrivateRoute> },
      { path: 'parent/children/:studentId', element: <PrivateRoute><ParentChildPage /></PrivateRoute> },
      { path: 'parent', element: <PrivateRoute><ParentHomePage /></PrivateRoute> },
      { path: 'settings/editor', element: <PrivateRoute><EditorSettingsPage /></PrivateRoute> },
      { path: 'invite/:token', element: <InvitePage /> },
      { path: 'parent-invite/:token', element: <ParentInvitePage /> },
      { path: 'child-invite/:token', element: <ChildInvitePage /> },
      { path: 'tasks', element: <PrivateRoute><StudentRoute><TaskListPage /></StudentRoute></PrivateRoute> },
      { path: 'oge-python', element: <ThemesRoute><OgePythonPathPage /></ThemesRoute> },
      { path: 'themes/osnovy_python_oge', element: <ThemesRoute><OgePythonPathPage /></ThemesRoute> },
      { path: 'sozdanie-igr', element: <ThemesRoute><GameCreationPathPage /></ThemesRoute> },
      { path: 'themes/sozdanie_igr', element: <ThemesRoute><GameCreationPathPage /></ThemesRoute> },
      { path: 'themes/:slug', element: <ThemesRoute><TopicProgramPage /></ThemesRoute> },
      { path: 'admin/topic-programs', element: <AdminRoute><AdminTopicProgramsPage /></AdminRoute> },
      { path: 'admin/task-cleanup', element: <AdminRoute><TaskBankCleanupPage /></AdminRoute> },
      { path: 'admin/articles', element: <AdminRoute><AdminArticlesPage /></AdminRoute> },
      { path: 'articles', element: <ArticlesListPage /> },
      { path: 'articles/:slug', element: <ArticleDetailPage /> },
      { path: 'catalog/print/:tagSlug', element: <PrivateRoute><TaskCatalogPrintPage /></PrivateRoute> },
      { path: 'catalog', element: <PrivateRoute><TaskCatalogPage /></PrivateRoute> },
      { path: 'practice-quizzes', element: <PracticeQuizzesPage /> },
      { path: 'trainers', element: <TrainersPage /> },
      { path: 'trainers/sessions/:sessionId', element: <TrainerSessionPage /> },
      { path: 'ege-24/okno', element: <SlidingWindowPage /> },
      { path: 'ege-13/rekursiya', element: <RecursionDemoPage /> },
      { path: 'ege-19-21/igra', element: <GameDemoPage /> },
      { path: 'tasks/:slug', element: <PrivateRoute><TaskDetailPage /></PrivateRoute> },
      { path: 'work/:taskSlug', element: <GuestOrPrivateWork /> },
      { path: 'sessions/:lessonSessionId/tasks/:taskSlug', element: <PrivateRoute><WorkPage /></PrivateRoute> },
      { path: 'my-tasks', element: <PrivateRoute><MyTasksPage /></PrivateRoute> },
      { path: 'my-attempts', element: <PrivateRoute><MyAttemptsPage /></PrivateRoute> },
      { path: 'my-lessons', element: <PrivateRoute><StudentRoute><MyLessonsPage /></StudentRoute></PrivateRoute> },
      { path: 'quiz-review/:listId', element: <PrivateRoute><QuizReviewPage /></PrivateRoute> },
      { path: 'my-schedule', element: <PrivateRoute><MySchedulePage /></PrivateRoute> },
      { path: 'my-messages', element: <PrivateRoute><MyMessagesPage /></PrivateRoute> },
      { path: 'konspekty/new', element: <PrivateRoute><KonspektFormPage /></PrivateRoute> },
      { path: 'konspekty/:slug/edit', element: <PrivateRoute><KonspektFormPage /></PrivateRoute> },
      { path: 'konspekty/:slug', element: <PrivateRoute><KonspektDetailPage /></PrivateRoute> },
      { path: 'konspekty', element: <PrivateRoute><KonspektyListPage /></PrivateRoute> },
      { path: 'my-teachers', element: <PrivateRoute><MyTeachersPage /></PrivateRoute> },
      { path: 'topic-progress', element: <PrivateRoute><StudentTopicProgressPage /></PrivateRoute> },
      { path: 'dashboard', element: <PrivateRoute><DashboardOrRedirect /></PrivateRoute> },
      { path: 'teacher/schedule', element: <EditorRoute><TeacherSchedulePage /></EditorRoute> },
      { path: 'teacher/messages', element: <EditorRoute><TeacherMessagesPage /></EditorRoute> },
      { path: 'teacher/billing/:studentId', element: <EditorRoute><TeacherStudentBillingPage /></EditorRoute> },
      { path: 'teacher/billing', element: <EditorRoute><TeacherBillingPage /></EditorRoute> },
      { path: 'teacher/students/:studentId/questionnaire', element: <EditorRoute><TeacherStudentQuestionnairePage /></EditorRoute> },
      { path: 'teacher/students/:studentId/activity', element: <EditorRoute><TeacherStudentActivityPage /></EditorRoute> },
      { path: 'teacher/students/:studentId/topic-progress', element: <EditorRoute><StudentTopicProgressPage /></EditorRoute> },
      { path: 'teacher/students/:studentId', element: <EditorRoute><TeacherStudentWorkPage /></EditorRoute> },
      { path: 'teacher/lists/create', element: <EditorRoute><TeacherListCreatePage /></EditorRoute> },
      { path: 'teacher/lists', element: <EditorRoute><TeacherDashboardPage /></EditorRoute> },
      { path: 'teacher/lessons', element: <EditorRoute><TeacherDashboardPage /></EditorRoute> },
      { path: 'teacher', element: <EditorRoute><TeacherDashboardPage /></EditorRoute> },
      { path: 'teacher/tasks', element: <EditorRoute><TaskEditorPage /></EditorRoute> },
      { path: 'teacher/tasks/import', element: <EditorRoute><BulkImportPage /></EditorRoute> },
      { path: 'teacher/tags', element: <EditorRoute><TeacherTagsPage /></EditorRoute> },
      { path: 'teacher/tools', element: <EditorRoute><TeacherToolsPage /></EditorRoute> },
      { path: 'teacher/task-inbox', element: <EditorRoute><TeacherTaskInboxPage /></EditorRoute> },
      { path: 'teacher/ege-families', element: <EditorRoute><TaskBankFamiliesPage /></EditorRoute> },
      { path: 'teacher/ege-map', element: <EditorRoute><TeacherEgeMapPage /></EditorRoute> },
      { path: 'teacher/users', element: <AdminRoute><AdminUsersPage /></AdminRoute> },
      { path: 'teacher/new-users', element: <AdminRoute><TeacherNewUsersPage /></AdminRoute> },
      { path: 'teacher/new-users/:userId/activity', element: <AdminRoute><TeacherNewUserActivityPage /></AdminRoute> },
      { path: 'collaborative', element: <CollaborativePage /> },
      { path: 'collaborative/:sessionId', element: <CollaborativePage /> },
      { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </ThemeProvider>
  );
}
